<template>
    <el-scrollbar class="outlook-home" v-loading="initializing">
        <router-view v-if="ready" />
        <div class="message-container" v-else-if="accountNotConnected">
            <div class="md-icon warning">warning</div>
            <div class="title">{{ $t('no_webcrm_account') }}</div>
            <div class="subtitle">{{ $t('please_connect_ms_account') }}</div>
            <el-button type="primary" @click="connectAccount">{{ $t('connect') }}</el-button>
            <el-button type="secondary" @click="restart">{{ $t('restart') }}</el-button>
        </div>
        <div class="message-container" v-else-if="invalidLicense">
            <div class="md-icon warning">warning</div>
            <div class="title">{{ $t('no_access').toLowerCase().replace(/^./, c => c.toUpperCase()) }}</div>
            <div class="subtitle">{{ $t('your_license_does_not_include_this_feature') }}</div>
            <el-button type="primary" @click="upgradeLicense">{{ $t('change') }} {{ $t('license').toLowerCase() }}</el-button>
        </div>
        <div class="message-container" v-else-if="!initializing">
            <div class="md-icon error">cancel</div>
            <div class="title">{{ $t('an_unexpected_error_occurred') }}</div>
            <el-button type="primary" @click="restart">{{ $t('restart') }}</el-button>
        </div>
    </el-scrollbar>
</template>

<script lang="ts">
    import Vue from 'vue';

    import { Context, LicenseType } from '@/utilities/contex';
    import { ApiError } from '@/utilities/api/client';
    import { NavigationProvider } from '@/utilities/navigation';
    import EventBus from '@/utilities/event-bus';

    export default Vue.extend({
        name: 'outlook-home',
        data() {
            return {
                initializing: true,
                ready: false,
                accountNotConnected: false,
                invalidLicense: false,
            }
        },
        async created() {
            await Office.onReady();
            this.setTheme();
            await this.init();
            window.addEventListener('blur', this.scheduleReinit);
            EventBus.$on(`Office.${Office.EventType.ItemChanged}`, this.redirectToHome);
        },
        destroyed() {
            window.removeEventListener('blur', this.scheduleReinit);
            EventBus.$off(`Office.${Office.EventType.ItemChanged}`, this.redirectToHome);
        },
        methods: {
            async init() {
                this.initializing = true;
                try {
                    await Context.init();

                    if (Context.user.licenseType < LicenseType.Plus) {
                        this.invalidLicense = true;
                        Context.reset();
                    } else {
                        this.ready = true;
                    }
                } catch (e) {
                    console.error(e);
                    this.accountNotConnected = e instanceof ApiError && e.status === 401 && e.error == 'External user not connected';
                }
                this.initializing = false;
            },
            scheduleReinit() {
                if (this.accountNotConnected || this.invalidLicense) {
                    window.addEventListener('focus', this.init, { once: true });
                }
            },
            setTheme() {
                document.body.classList.toggle('dark-theme', Office.context.officeTheme?.bodyBackgroundColor
                    ? (Office.context.officeTheme.bodyBackgroundColor?.match(/#(\w{2})(\w{2})(\w{2})/)?.splice(1)?.map(c => parseInt(c, 16))?.reduce((a, b) => a + b, 0) || 255 * 3) / 3 / 255 < 0.5
                    : window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches);
            },
            upgradeLicense() {
                NavigationProvider.go(NavigationProvider.getLicensePageUrl());
            },
            connectAccount() {
                NavigationProvider.go(NavigationProvider.getMicrosoftLoginUrl());
            },
            restart() {
                location.reload();
            },
            redirectToHome() {
                if (this.$route.path != '/outlook') {
                    this.$router.push({ path: '/outlook' });
                }
            },
        }
    });

</script>

<style scoped lang="scss">

    .outlook-home {
        position: absolute;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        background-color: var(--white);

        & /deep/ > .el-scrollbar__wrap {
            overflow-y: scroll;
            overflow-x: hidden;

            & > .el-scrollbar__view {
                padding: 16px;
            }
        }
    }

    .message-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 12px;
        text-align: center;

        .title {
            @extend %headline-h2;
        }

        .subtitle {
            @extend %body-16-regular;
            color: var(--grey-700);
        }

        .md-icon {
            font-size: 48px;

            &.warning {
                color: var(--info-500);
            }

            &.error {
                color: var(--danger-500);
            }
        }

        & > * {
            margin-left: 0 !important;
            margin-right: 0 !important;
        }
    }

</style>